'use client';

import clsx from 'clsx';
import { useField, useFormikContext } from 'formik';
import { type ComponentPropsWithRef, useEffect, useId, useState } from 'react';

import BaseContainer from '@commons/containers/BaseContainer';

import Label from '../Label';
import styles from './index.module.scss';

/**
 * TextField component props.
 */

export type TextFieldProps = ComponentPropsWithRef<'input'> & {
  name: string;
  type?: 'text' | 'number' | 'email' | 'date';
  error?: boolean;
  required?: boolean;
  label?: string;
  placeholder?: string;
  variant?: 'primary' | 'secondary';
};

/**
 * TextField component.
 *
 * This export can be used as an uncontrolled component or with a custom controller.
 */

export const TextField = (props: TextFieldProps) => {
  const generatedId = useId();
  const [hasValue, setHasValue] = useState(false);

  const {
    type = 'text',
    id,
    error = false,
    className,
    label,
    placeholder,
    required = false,
    variant = 'primary',
    ref,
    ...rest
  } = props;

  const fieldId = id ?? generatedId;

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setHasValue(!!event.target.value);
  };

  const handleFocus = () => {
    setHasValue(true);
  };

  const classes = clsx(
    styles.textField,
    styles[variant],
    error && styles.error,
    label && styles.hasLabel,
    className
  );

  return (
    <BaseContainer className={styles.wrapper}>
      <input
        id={fieldId}
        placeholder={placeholder}
        type={type}
        className={classes}
        aria-required={required || undefined}
        {...rest}
        ref={ref}
        onBlur={handleBlur}
        onFocus={handleFocus}
      />
      {label && (
        <Label
          htmlFor={fieldId}
          active={hasValue || !!rest.value}
          required={required}
          error={error}
          className={clsx(
            styles.label,
            styles[variant],
            (hasValue || !!rest.value) && styles.shrink
          )}
        >
          {label}
        </Label>
      )}
    </BaseContainer>
  );
};

/**
 * Formik-controlled TextField component.
 *
 * This export should be used inside a Formik form.
 */

export const FormikTextField = ({ name, placeholder, label, ...props }: TextFieldProps) => {
  const [field, meta] = useField(name);
  const { setFieldValue } = useFormikContext();

  const isError = meta.touched && meta.error !== undefined;

  // This effect will check if there's a value in the URL for this field
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search);
      const urlValue = searchParams.get(name);

      if (urlValue) {
        setFieldValue(name, urlValue);
      }
    }
  }, [name, setFieldValue]);

  return (
    <TextField error={isError} placeholder={placeholder} {...field} {...props} label={label} />
  );
};

export default TextField;
